import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { TradingHistoryViewedProperties } from '@iwoca/frontend-tracking-library/lib/ampli';
import { Card } from '@iwoca/orion';
import { clsx } from 'clsx';

import { LoanSummary } from './LoanSummary/LoanSummary';
import { LoanSummaryLoader } from './LoanSummaryLoader';
import AgedDebtorReport from './RequirementTypes/AgedDebtorReport';
import BusinessPlan from './RequirementTypes/BusinessPlan';
import CompanyAccounts from './RequirementTypes/CompanyAccounts';
import ManagementAccounts from './RequirementTypes/ManagementAccounts';
import VATReturns from './RequirementTypes/VatReturns/VATReturns';
import ShareRequirements from './ShareRequirements/ShareRequirements';
import styles from './TradingHistoryUK.module.css';
import { amplitudeDocRequirementStatusEventProperties } from './TradingHistoryUK.tracking.helper';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useOffersUK } from '../../../hooks/useOffers/useOffersUK';
import { useSignupParams } from '../../../hooks/useSignupParams';
import { useSignupRouterUK } from '../../../hooks/useSignupRouter/useSignupRouterUK';
import {
  useGetApplicationQuery,
  useSplitTestBranch,
} from '../../../lendingApi/lendingApiQueries';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import BackToStep from '../../BackToStep/BackToStep';
import { Button } from '../../Button/Button';
import SaveProgressButton from '../../SaveProgressButton/SaveProgressButton';
import SubmitButton from '../SubmitDocumentsButton/SubmitDocumentsButtonUK/SubmitDocumentsButtonUK';
import { TradingHistoryLoader } from '../TradingHistoryLoader';
import { VATReturnsSwitch } from './RequirementTypes/VatReturns/VATReturnsSwitch';
import { useSplitTestContext } from '../../../hooks/useSplitTestContext';
import BankStatementOpenBanking from '../SharedRequirementTypes/BankStatements/BankStatementsOpenBanking';

export type TVatSwitchStatus = 'yes' | 'no';
export type TVatUploadStatus = 'pending' | 'required' | 'optional';

const DOCUMENT_REQUIREMENTS: { [key: string]: React.ElementType } = {
  company_accounts: CompanyAccounts,
  management_accounts: ManagementAccounts,
  business_plan: BusinessPlan,
  aged_debtor_report: AgedDebtorReport,
  vat_return: VATReturns,
  bank_statement: BankStatementOpenBanking,
  via_open_banking: BankStatementOpenBanking,
};

const TradingHistoryUK = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { isDirectCustomer, isPartnerCustomer } = useSignupParams();

  const stateKey = useContextStateKey();
  const { navigateSignup } = useSignupRouterUK();

  const { application } = useGetApplicationQuery();

  const removeVatRequirementTest = useSplitTestBranch(
    'remove_vat_requirement_test',
    {
      enabled: true,
    },
  );

  const { hasBusinessDetailsReductionEnabled } = useSplitTestContext();

  const {
    documentRequirements,
    isLoadingOffers,
    shouldShowOffers,
    offers,
    isAutoEligible,
  } = useOffersUK({
    enabled: true,
  });

  const shouldShowVatReturnsSwitch =
    hasBusinessDetailsReductionEnabled &&
    removeVatRequirementTest.splitTestBranch === 'EXCLUDE' &&
    !isAutoEligible;

  const [vatSwitchStatus, setVatSwitchStatus] = useState<'yes' | 'no'>('yes');
  const [vatUploadStatus, setVatUploadStatus] =
    useState<TVatUploadStatus>('optional');

  useEffect(() => {
    setVatUploadStatus(shouldShowVatReturnsSwitch ? 'required' : 'optional');
  }, [shouldShowVatReturnsSwitch]);

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Trading history',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  useEffect(() => {
    const suggestedOffer = offers?.find((offer) => offer.is_suggested);

    if (
      !application?.requested_products?.[0] ||
      !documentRequirements ||
      !suggestedOffer
    ) {
      return;
    }

    const requestedProduct = application.requested_products[0];

    const eventProperties: Pick<
      TradingHistoryViewedProperties,
      | 'requested_amount'
      | 'loan_probability'
      | 'suggested_amount'
      | 'Page version'
      | 'Requested product type'
    > = {
      // @ts-expect-error it can be null in the schema
      requested_amount: requestedProduct.amount,
      loan_probability: suggestedOffer.probability_of_offer,
      suggested_amount: requestedProduct.suggested_product?.amount,
      'Page version': 'v2',
      'Requested product type': requestedProduct.product_type || '',
    };

    tracking.ampli.tradingHistoryViewed({
      ...eventProperties,
      ...amplitudeDocRequirementStatusEventProperties(documentRequirements),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(documentRequirements)]);

  const optionalDocumentRequirements = (() => {
    if (!documentRequirements) return [];
    if (isDirectCustomer) return [];

    return documentRequirements?.filter((document) => document.is_optional);
  })();

  const requiredDocumentRequirements =
    documentRequirements?.filter((document) => !document.is_optional) || [];

  const renderContents = () => {
    if (
      (isLoadingOffers && !documentRequirements) ||
      removeVatRequirementTest.isLoading
    ) {
      return <TradingHistoryLoader />;
    }

    if (!documentRequirements) {
      return;
    }

    return (
      <>
        <div className={styles.documentsContainer}>
          {requiredDocumentRequirements?.map((requirement) => {
            if (!DOCUMENT_REQUIREMENTS[requirement.type]) {
              return null;
            }

            if (
              shouldShowVatReturnsSwitch &&
              requirement.type === 'vat_return'
            ) {
              return null;
            }

            const RequirementDetails = DOCUMENT_REQUIREMENTS[requirement.type];

            return (
              <RequirementDetails
                key={requirement.type}
                quantifier={requirement.quantifier?.value}
                resolutionMethods={requirement.resolution_methods}
                status={requirement.status}
                isSubmitted={isSubmitted}
              />
            );
          })}
          {shouldShowVatReturnsSwitch && (
            <VATReturnsSwitch
              documentRequirements={documentRequirements}
              isSubmitted={isSubmitted}
              setSwitchStatus={setVatSwitchStatus}
              switchStatus={vatSwitchStatus}
              setUploadStatus={setVatUploadStatus}
              uploadStatus={vatUploadStatus}
            />
          )}
        </div>
        <Card className={clsx(styles.cardContainer, styles.shareReqCard)}>
          <ShareRequirements
            requiredDocs={requiredDocumentRequirements}
            optionalDocs={optionalDocumentRequirements}
          />
        </Card>
        <div className={styles.buttonsContainer}>
          {isDirectCustomer && <BackToStep />}
          {shouldShowOffers && isPartnerCustomer && (
            <Button
              variant="tertiary"
              className={styles.button}
              disabled={!stateKey}
              onClick={() => navigateSignup({ route: 'offers' })}
            >
              Back
            </Button>
          )}
          {isPartnerCustomer && <SaveProgressButton />}
          <SubmitButton
            isSubmitted={isSubmitted}
            setIsSubmitted={setIsSubmitted}
            vatUploadRequired={
              vatSwitchStatus === 'yes' && vatUploadStatus === 'required'
            }
          />
        </div>
      </>
    );
  };

  return (
    <div className={styles.tradingHistory}>
      <div className={styles.loanSummaryContainer}>
        {!isLoadingOffers && offers ? (
          <LoanSummary application={application} offers={offers} />
        ) : (
          <LoanSummaryLoader />
        )}
      </div>
      <div className={styles.headingContainer}>
        <h1 className={styles.heading}>Add your trading history</h1>
        <p className={styles.subHeading}>
          {isLoadingOffers && 'Loading your required documents...'}
        </p>
      </div>
      {renderContents()}
    </div>
  );
};

export default TradingHistoryUK;
